import Box from "@mui/material/Box";
import Tooltip, {
	tooltipClasses,
	type TooltipProps,
} from "@mui/material/Tooltip";
import { styled } from "@mui/material/styles";
import { Text, useTheme } from "react-native-paper";
import { useAppTheme } from "../../useAppTheme";
import Button from "../Button";

type Props = {
	title: string;
	children: React.ReactElement;
	style?: React.CSSProperties;
	rich?: boolean;
	subtitle?: string;
	primaryAction?: {
		label: string;
		onPress: () => void;
	};
	secondaryAction?: {
		label: string;
		onPress: () => void;
	};
};

const StyledTooltip = styled(({ className, ...props }: TooltipProps) => (
	<Tooltip {...props} classes={{ popper: className }} />
))(() => {
	const paperTheme = useAppTheme();
	return {
		[`& .${tooltipClasses.tooltip}`]: {
			color: paperTheme.colors.inverseOnSurface,
			backgroundColor: paperTheme.colors.inverseSurface,
		},
	};
});

const TooltipWrapper = ({
	title,
	children,
	style,
	rich = false,
	subtitle,
	primaryAction,
	secondaryAction,
}: Props) => {
	return rich ? (
		<RichTooltip
			enterTouchDelay={0}
			leaveTouchDelay={2000}
			title={
				<Container
					title={title}
					subtitle={subtitle}
					primaryAction={primaryAction}
					secondaryAction={secondaryAction}
				/>
			}
		>
			<div style={{ cursor: "help", ...style }}>{children}</div>
		</RichTooltip>
	) : (
		<StyledTooltip
			role="tooltip"
			title={title}
			enterTouchDelay={0}
			leaveTouchDelay={2000}
		>
			<div
				style={{
					cursor: "help",
					...style,
				}}
			>
				{children}
			</div>
		</StyledTooltip>
	);
};
export default TooltipWrapper;

const RichTooltip = styled(({ className, ...props }: TooltipProps) => (
	<Tooltip role="tooltip" {...props} classes={{ popper: className }} />
))(() => ({
	[`& .${tooltipClasses.tooltip}`]: {
		backgroundColor: "transparent",
	},
}));

type ContainerProps = {
	title: string;
	subtitle?: string;
	primaryAction?: {
		label: string;
		onPress: () => void;
	};
	secondaryAction?: {
		label: string;
		onPress: () => void;
	};
};

const Container = ({
	title,
	subtitle,
	primaryAction,
	secondaryAction,
}: ContainerProps) => {
	const theme = useTheme();
	return (
		<Box
			style={{
				backgroundColor: theme.colors.surface,
				borderRadius: 12,
				paddingBottom: 8,
				boxShadow: `0px 2px 6px 2px ${theme.colors.shadow}15, 0px 1px 2px 0px ${theme.colors.shadow}30`,
			}}
		>
			<Box display="flex" flexDirection="column" style={{ padding: 16 }}>
				{title && (
					<Text
						style={{ color: theme.colors.onSurfaceVariant }}
						variant="titleSmall"
					>
						{title}
					</Text>
				)}
				{subtitle && (
					<Text
						variant="bodyMedium"
						style={{ paddingTop: 4, color: theme.colors.onSurfaceVariant }}
					>
						{subtitle}
					</Text>
				)}
			</Box>
			<Box style={{ display: "flex", gap: 8, paddingLeft: 8, paddingRight: 8 }}>
				{primaryAction && (
					<Button mode="text" onPress={primaryAction?.onPress}>
						{primaryAction?.label}
					</Button>
				)}
				{secondaryAction && (
					<Button mode="text" onPress={secondaryAction?.onPress}>
						{secondaryAction?.label}
					</Button>
				)}
			</Box>
		</Box>
	);
};
